import React from "react";
import { Box } from "@mui/joy";
import type { AlertItem } from "../../../api/zekay/alerts";
import type { MenuTileCardId, MenuTileUi } from "../../../api/zekay/menu";
import type { MenuGroup } from "../config/menuConfig";
import { getMenuCardTitle } from "../config/menuConfig";
import {
  menuCategoryInnerTilesGridSx,
  menuCategoryPanelSx,
  menuCategoryTileCellSx,
  menuCategoryTitleRowSx,
  menuPageCardGroupsGridSx,
} from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import MenuTileCard from "./MenuTileCard";

type Props = {
  Groups: MenuGroup[];
  IsMobile: boolean;
  ActiveIdx: number;
  Alerts: AlertItem[];
  IsAlertsLoading: boolean;
  GetForPath: (pathname: string) => { enabled: boolean; categories: string[] };
  OnTileClick: (path: string) => void;
  OnTouchStart: (e: React.TouchEvent) => void;
  OnTouchEnd: (e: React.TouchEvent) => void;
};

export default function MenuTilesGrid({
  Groups,
  IsMobile,
  ActiveIdx,
  Alerts,
  IsAlertsLoading,
  GetForPath,
  OnTileClick,
  OnTouchStart,
  OnTouchEnd,
}: Props) {
  const renderTile = (tile: MenuTileUi, index: number) => {
    const ui = GetForPath(tile.path);
    const contextual = Alerts.filter((a) => a.category && ui.categories.includes(a.category));
    const triggered = contextual.filter((a) => a.triggered);
    const badgeCount = triggered.reduce((sum, a) => sum + (a.count || 0), 0);
    const badgeLoading = !!tile.alertsEnabled && ui.enabled && IsAlertsLoading;

    return (
      <Box key={tile.path || `tile-${String(index)}`} sx={menuCategoryTileCellSx}>
        <MenuTileCard
          Tile={tile}
          BadgeCount={badgeCount}
          BadgeLoading={badgeLoading}
          OnClick={() => OnTileClick(tile.path)}
        />
      </Box>
    );
  };

  const renderPanel = (id: MenuTileCardId, tiles: MenuTileUi[]) => (
    <Box key={id} sx={menuCategoryPanelSx} role="group" aria-label={getMenuCardTitle(id)}>
      <Box sx={menuCategoryTitleRowSx}>
        <DarkTypography
          level="title-sm"
          colorVariant="title"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "0.95rem", sm: "1rem" },
            lineHeight: 1.25,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
          component="h2"
        >
          {getMenuCardTitle(id)}
        </DarkTypography>
      </Box>
      <Box sx={menuCategoryInnerTilesGridSx}>{tiles.map((tile, i) => renderTile(tile, i))}</Box>
    </Box>
  );

  if (!IsMobile) {
    return (
      <Box sx={menuPageCardGroupsGridSx} component="section" aria-label="Modules">
        {Groups.map(({ id, tiles }) => renderPanel(id, tiles))}
      </Box>
    );
  }

  const current = Groups[ActiveIdx];

  return (
    <Box
      component="section"
      aria-label="Modules"
      onTouchStart={OnTouchStart}
      onTouchEnd={OnTouchEnd}
      sx={{
        width: "100%",
        maxWidth: "min(100%, 95vw)",
        mx: "auto",
        px: 0.75,
        boxSizing: "border-box",
      }}
    >
      {current && renderPanel(current.id, current.tiles)}
    </Box>
  );
}
